"use client";

import { ChevronRight, Sparkles } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { getMockNewsItems } from "@/dummy";
import { useVisitorNewsListSlimQuery } from "@/lib/graphql/queries/generated/news.generated";
import { useConfigLocalizer } from "@/lib/localize-config-text";
import { cacheKeys } from "@/lib/offline-cache";
import { useDemoContent } from "@/lib/use-demo-translator";
import { useOfflineData } from "@/lib/use-offline-data";
import { useWidgetEnv } from "@/lib/use-widget-env";
import type { HomeBlock, WidgetTab } from "@/lib/widget-config";
import { useWidgetInstance } from "@/lib/widget-instance-context";
import { extractNewsItemsFromList, extractNewsItemsFromRaw } from "../helpers";
import { BlockCard } from "../shared";

export function NewsPreviewBlock({
  block,
  onOpenTab,
  onOpenNewsArticle,
  isDummy,
}: {
  block: Extract<HomeBlock, { kind: "news-preview" }>;
  onOpenTab: (tab: WidgetTab) => void;
  /** Open a specific post. Falls back to the News tab when unset. */
  onOpenNewsArticle?: (id: string) => void;
  isDummy: boolean;
}) {
  const t = useTranslations("home");
  const tDemo = useDemoContent();
  const locale = useLocale();
  const env = useWidgetEnv();
  const localize = useConfigLocalizer();
  // Honour the operator's `maxItems` knob, capped at 10 for UI sanity
  // (more than that dominates the home tab — operators wanting a long
  // feed should link visitors into the News tab via `news-preview`'s
  // "See all" affordance, not stuff the home tab).
  const max = Math.max(1, Math.min(10, block.maxItems ?? 3));

  // Same prefetch short-circuit as the News tab — render the
  // dashboard-supplied list instead of hitting the visitor feed when
  // the preview pane has already loaded the operator's posts.
  // Empty arrays don't count (still-loading edge case) — fall through
  // to the BE query so the block doesn't render permanently empty.
  const previewFeed = !isDummy ? env.previewData?.newsPosts : undefined;
  const hasPreview = Array.isArray(previewFeed) && previewFeed.length > 0;

  // Real path queries the consolidated `visitorNewsList` the News
  // tab uses. Skipped in dummy mode — we render bundled
  // getMockNewsItems() instead so the dashboard preview's "demo" toggle
  // gets a populated card.
  // Slim query — the home preview renders a title per row and opens the
  // tapped post directly (it carries the `_id`), so cover image / authors /
  // tags / categories are all over-fetch we skip on the home tab.
  const { data, error, loading } = useVisitorNewsListSlimQuery({
    variables: { args: { locale, limit: max } },
    skip: isDummy || hasPreview,
  });
  // Offline READ: cache the fetched preview so the home tab still shows it when
  // the BE is down (same as the full News tab).
  const newsPk = useWidgetInstance().publicKey;
  const effectiveData = useOfflineData(
    newsPk && !isDummy && !hasPreview ? cacheKeys.home(newsPk, `news-slim:${max}`, locale) : null,
    data,
  );

  const items = isDummy
    ? getMockNewsItems(tDemo)
        .slice(0, max)
        .map((n) => ({ id: n.id, title: n.title }))
    : hasPreview
      ? extractNewsItemsFromRaw(previewFeed).slice(0, max)
      : extractNewsItemsFromList(effectiveData?.visitorNewsList?.items, error).slice(0, max);

  // Live mode + no items → hide the card entirely rather than showing a
  // header with nothing under it. Loading shows the header but no rows so
  // the layout doesn't jump when items arrive.
  if (!isDummy && !loading && items.length === 0) return null;

  return (
    <BlockCard
      title={localize(block.title, t("newsPreviewTitle"))}
      onOpenAll={() => onOpenTab("news")}
    >
      <ul className="flex flex-col">
        {items.map((item) => (
          <li key={item.id || item.title}>
            <button
              type="button"
              onClick={() => (onOpenNewsArticle ? onOpenNewsArticle(item.id) : onOpenTab("news"))}
              className="flex w-full items-center gap-2 px-3.5 py-2.5 text-start transition-colors hover:bg-wx-bg-elevated-2"
            >
              <Sparkles size={14} className="shrink-0 text-wx-fg-muted" />
              <span className="min-w-0 flex-1 truncate text-sm text-wx-fg">{item.title}</span>
              <ChevronRight size={14} className="shrink-0 text-wx-fg-subtle rtl:-scale-x-100" />
            </button>
          </li>
        ))}
      </ul>
    </BlockCard>
  );
}
